import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartDisplay, DynamicChartData, Filters } from "../../types"
import * as Enums from "../../util/Enums";
import { Fragment, useEffect, useMemo, useState } from "react";
import { presetColors, presetSpecificColors } from "../../util/Colours";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

type GraphAreaProps = {
    chartData: DynamicChartData[];
    filters: Filters;
    chartDisplay: ChartDisplay;
    groups: string[];
    chartRef: React.MutableRefObject<HTMLDivElement | null>;
    metricLabel: string;
}

function GraphArea({chartData, filters, chartDisplay, groups, chartRef, metricLabel} : GraphAreaProps) {
    function darkenHex(hex: string, percent: number) : string {
        // Remove the '#' character if present
        hex = hex.replace(/^#/, '');
        
        // Parse the hex color into its RGB components
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        
        // Calculate the amount to darken each component by
        r = Math.floor(r * (1 - percent / 100));
        g = Math.floor(g * (1 - percent / 100));
        b = Math.floor(b * (1 - percent / 100));
        
        // Convert each component back to a two-digit hex string
        const rS = r.toString(16).padStart(2, '0');
        const gS = g.toString(16).padStart(2, '0');
        const bS = b.toString(16).padStart(2, '0');
        
        // Concatenate the darkened components and return the result
        return `#${rS}${gS}${bS}`;
    }

    const unit = useMemo(() => {
        const maxValue = Math.max(
            ...chartData.flatMap(data =>
                groups.map(group => {
                    const value = data[group];
                    return typeof value === 'number' ? value : 0; // Ensure we only take numeric values
                })
            )
        );
        if (maxValue >= 1e6) {
            return metricLabel === 'kWh' ? 'GWh' : `GWh/${metricLabel}`;
        } else if (maxValue >= 1e3) {
            return metricLabel === 'kWh' ? 'MWh' : `MWh/${metricLabel}`
        } else {
            return metricLabel === 'kWh' ? 'kWh' : `kWh/${metricLabel}`;
        }
    }, [chartData, groups]);

    const numberFormatter = (value: number) => {
        if (unit === 'GWh') {
            return `${(value / 1e6).toFixed(2)}`; // Format as GWh
        } else if (unit === 'MWh') {
            return `${(value / 1e3).toFixed(2)}`; // Format as MWh
        } else {
            return `${value.toFixed(2)}`; // Format as kWh
        }
    };

    const dateFormatter = (value: any) => {
        if (!moment(value, moment.ISO_8601, true).isValid())
            return value;

        switch (filters?.period) {
            case Enums.PeriodEnum.HOURLY:
                return moment(value).format('DD/MM/YYYY HH:mm');
            case Enums.PeriodEnum.DAILY:
                return moment(value).format('DD/MM/YYYY');
            case Enums.PeriodEnum.WEEKLY:
                return moment(value).format('DD/MM/YYYY');
            case Enums.PeriodEnum.MONTHLY:
                if (!filters.startDate || !filters.endDate) {
                    return moment(value).format('MMM YYYY');
                } else {
                    const startDate = moment(filters.startDate);
                    const endDate = moment(filters.endDate);
                    const monthDiff = endDate.diff(startDate, 'months');
                    return monthDiff < 12 ? moment(value).format('MMM') : moment(value).format('MMM YYYY');
                }
            case Enums.PeriodEnum.YEARLY:
                return moment(value).format('YYYY');
            default:
                return value;
        }
    };

    // get unique colours for each group - use specific group colours when necessary
    const indexedColours: string[] = []; // array of indexed colours to use in chart
    let copyPresetColors = [...presetColors]; // copy of preset colours that can be reset when all used
    groups.forEach((group) => {
        console.log(group)
        // if group has a specified colour - use this and remove it from the copyPresetColours if exists
        if (Object.keys(presetSpecificColors).includes(group)) {
            const specifiedColor = presetSpecificColors[group];
            indexedColours.push(specifiedColor);
            if (copyPresetColors.includes(specifiedColor)) copyPresetColors.splice(copyPresetColors.indexOf(specifiedColor), 1)
        } else {
            // use the first value left in copy of preset colours
            const color = copyPresetColors.shift();
            if (color !== undefined) indexedColours.push(color);
        }
        if (copyPresetColors.length === 0) copyPresetColors = [...presetColors]; // if preset colours all used - then reset to original values
        // NOTE: could have rare issue where a specified colour is first in presetColors and the copy of this is reset just after the specified used - not a big deal
    });

    const getDayNightHex = (date: Date, i: number) : string => {
        //!!! Not sure why it needs utc hours here to format correctly - worth having a look into
        return date.getUTCHours() >= 0 && date.getUTCHours() <= 6 ? darkenHex(indexedColours[i], 30) : indexedColours[i];
    };

    // legend-driven highlighting
    const [hoverKey, setHoverKey] = useState<string | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const focusKey = selectedKey ?? hoverKey;
    const isActive = (key: string) => !focusKey || focusKey === key;
    const seriesOpacity = (key: string) => (isActive(key) ? 1 : 0.2);

    useEffect(() => {
        // Reset hover and selected keys on unmount
        setHoverKey(null);
        setSelectedKey(null);
    }, [groups]);

    // custom legend that preserves default formatting (centered, color swatch beside text, text colored to match, no underlining)
    const renderLegend = (props: any) => {
        const { payload } = props || {};
        if (!payload) return null;
        return (
            <ul className="recharts-default-legend" style={{ margin: 0, padding: 0, textAlign: 'center' }}>
                {payload.map((entry: any, i: number) => {
                    const key = String(entry?.dataKey ?? entry?.value);
                    const isSelected = selectedKey === key;
                    const isHovered = hoverKey === key;
                    const dim = (!!selectedKey && !isSelected) || (!!hoverKey && !isHovered);
                    return (
                        <li
                            key={`legend-item-${i}`}
                            className="recharts-legend-item"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginRight: 10,
                                cursor: 'pointer',
                                opacity: dim ? 0.5 : 1
                            }}
                            onMouseEnter={() => setHoverKey(key)}
                            onMouseLeave={() => setHoverKey(null)}
                            onClick={() => setSelectedKey(prev => prev === key ? null : key)}
                        >
                            <svg width="14" height="14" viewBox="0 0 32 32" style={{ marginRight: 4 }}>
                                <path fill={entry.color} d="M0,4h32v24H0z" />
                            </svg>
                            <span style={{ color: entry.color, display: 'inline-block' }}>{entry?.value}</span>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <ResponsiveContainer ref={chartRef} width="100%" height="100%">
            <ComposedChart data={chartData} margin={{top: 20, left: 40, right: 40 }}>
                <XAxis dataKey="date" tickFormatter={dateFormatter} />
                <YAxis tickFormatter={numberFormatter} label={{ value: unit, angle: -90, position: 'left', offset:20 }}/>
                <Tooltip formatter={numberFormatter} labelFormatter={dateFormatter} itemSorter={(item: any) => -Number(item?.value ?? -Infinity)} />
                <CartesianGrid strokeDasharray="3 3" />
                <ReferenceLine y={0} stroke="#000" />
                <Legend content={renderLegend} />
                {groups.map((group, index) => (
                    <Fragment key={uuidv4()}>
                        {chartDisplay?.chartType === Enums.ChartTypeEnum.LINE &&
                            <Line strokeWidth={3} type="monotone" dataKey={group} name={group} key={group} stroke={indexedColours[index]} strokeOpacity={seriesOpacity(group)} fillOpacity={0.3} fill={indexedColours[index]}
                                onMouseEnter={() => setHoverKey(group)}
                                onMouseLeave={() => setHoverKey(null)}
                                onClick={() => setSelectedKey(prev => prev === group ? null : group)}
                            />
                        }
                        {/* !!! This is just an example of day/night overlay - need an improved method for production  */}
                        {chartDisplay?.chartType === Enums.ChartTypeEnum.BAR && !(filters?.period === Enums.PeriodEnum.HOURLY && chartDisplay?.overlays.includes(Enums.OverlayEnum.DAY_NIGHT)) &&
                            <Bar type="monotone" dataKey={group} name={group} key={group} fill={indexedColours[index]} fillOpacity={seriesOpacity(group)}
                                onMouseEnter={() => setHoverKey(group)}
                                onMouseLeave={() => setHoverKey(null)}
                                onClick={() => setSelectedKey(prev => prev === group ? null : group)}
                            />
                        }
                        {chartDisplay?.chartType === Enums.ChartTypeEnum.BAR && filters?.period === Enums.PeriodEnum.HOURLY && chartDisplay?.overlays.includes(Enums.OverlayEnum.DAY_NIGHT) &&
                            <Bar type="monotone" dataKey={group} name={group} key={group} fill={indexedColours[index]} fillOpacity={seriesOpacity(group)}
                                onMouseEnter={() => setHoverKey(group)}
                                onMouseLeave={() => setHoverKey(null)}
                                onClick={() => setSelectedKey(prev => prev === group ? null : group)}
                            >
                                {chartData.map((entry, i) => (
                                    <Cell fill={getDayNightHex(entry.date, index)} key={`cell-${i}`} />
                                ))}
                            </Bar>
                        }
                    </Fragment>
                ))}
                {chartDisplay?.overlays.includes(Enums.OverlayEnum.TREND) && 
                    <Line strokeDasharray={"5 5"} type="monotone" dataKey="trend" name="Rolling Average" stroke="red" dot={false} strokeOpacity={seriesOpacity('trend')}
                        onMouseEnter={() => setHoverKey('trend')}
                        onMouseLeave={() => setHoverKey(null)}
                        onClick={() => setSelectedKey(prev => prev === 'trend' ? null : 'trend')}
                    />
                }
                {chartDisplay?.overlays.includes(Enums.OverlayEnum.AVERAGE) && 
                    <Line strokeDasharray={"5 5"} type="monotone" dataKey="overallAverage" name="Average" stroke="blue" dot={false} strokeOpacity={seriesOpacity('overallAverage')}
                        onMouseEnter={() => setHoverKey('overallAverage')}
                        onMouseLeave={() => setHoverKey(null)}
                        onClick={() => setSelectedKey(prev => prev === 'overallAverage' ? null : 'overallAverage')}
                    />
                }
            </ComposedChart>
        </ResponsiveContainer>
    )
}

export default GraphArea
